// ====================================================================
// — 「modal 滚动位置守门」(modal scroll position guard)
//
// 修的 bug: Flarum 2.0 移动端 (iOS) 打开任意 modal 后, 底层页面跳回顶部。
//
// ────────────────────────────────────────────────────────────────────
// 根因 (已在生产 bjxy.top + 本地 Chromium 实证, 不是推测)
// ────────────────────────────────────────────────────────────────────
//   1. Flarum core (js/package.json) 依赖 `body-scroll-lock@^4.0.0-beta.0`,
//      core 的 js/dist 是**预编译**的, 该库已被 webpack 打进去 ——
//      vendor 里没有 node_modules, 改 vendor 这条路不通。
//   2. 该库的 **ESM build** 里 setPositionFixed() 写的是:
//          document.body.style.position = 'fixed';
//          document.body.style.top  = -scrollY;   // 🔴 缺 px 单位
//          document.body.style.left = -scrollX;   // 🔴 缺 px 单位
//      CSSOM 对 <length> 要求单位, 无单位非零值被**直接丢弃**
//      (实测 Chromium: `body.style.top = -600` → `body.style.top === ''`)。
//   3. 于是 iOS 分支把 body 设成 position:fixed 但 top 无效
//      ⇒ body 脱离文档流 ⇒ documentElement.scrollHeight 塌缩
//      (实测 5312 → 844) ⇒ **滚动位置被 clamp 到 0** ⇒ 页面跳回顶部。
//   4. 关闭时 restorePositionSetting() 里
//          y = -parseInt(body.style.top, 10)   // '' ⇒ NaN
//      ⇒ window.scrollTo(0, NaN)
//      (实测 Chromium: scrollTo(0, NaN) 真的把位置清到 0)
//      ⇒ 滚动位置彻底回不来。
//   5. 触发需三者同时成立:
//      ① iOS (navigator.platform 命中 /iP(ad|hone|od)/ 或 MacIntel+多点触控)
//      ② 页面已滚动 (scrollY > 0)
//      ③ 打开任意 modal。
//      scrollY=0 时 -0 是合法的无单位零 ⇒ 不跳, 所以「在页面顶部开 modal 没事」。
//   6. 桌面 / Android 走另一个分支 (只设 body.style.overflow='hidden', 不设 position,
//      关闭时也没有 scrollTo) ⇒ 完全正常。
//   7. 上游已知 bug: https://github.com/willmcpo/body-scroll-lock/issues/271
//      「ESM build broken, causes scroll-to-top on iOS」。
//      源码早已修好 (-scrollY + 'px'), 但**发布出去的 ESM build 卡在旧版**,
//      而 Flarum 用的正是那个 ESM build。
//   8. Flarum core 2.x 最新就是 v2.0.2, 没有修了的新版可升 ⇒ 只能在我们自己扩展里补。
//
// ────────────────────────────────────────────────────────────────────
// 本补丁怎么修 (方案 A: CSS + 一小段 JS, 不碰 vendor, 不 monkey patch core)
// ────────────────────────────────────────────────────────────────────
//   · CSS (less/forum.less 末尾段):
//       body:has(#app[aria-hidden="true"]) { position: static !important; top: auto !important; left: auto !important; }
//     `#app[aria-hidden="true"]` 是 **Flarum core 自己的 ModalManager 加的信号**
//     (vendor/flarum/core/js/src/common/components/ModalManager.tsx:119 setAttribute /
//      :107 removeAttribute) ⇒ 不碰 core 内部 API、不改 vendor。
//     作者样式表里的 !important 优先级**高于** body-scroll-lock 运行时写的 inline style,
//     所以能把 body 从 position:fixed 拉回 static ⇒ body 不脱离文档流
//     ⇒ scrollHeight 不塌缩 ⇒ 滚动位置不会被 clamp 到 0。
//   · JS (本文件): 用 MutationObserver 盯 #app 的 aria-hidden,
//     打开时记下 window.scrollX/scrollY, 关闭时补回被 `scrollTo(0, NaN)` 毁掉的位置。
//
// ────────────────────────────────────────────────────────────────────
// 🔴🔴 移除条件 (辉哥 2026-10-19 明确要求写全) —— 将来官方修了这个 bug 就移除本修正
// ────────────────────────────────────────────────────────────────────
//   本补丁是针对 **Flarum core 依赖的 body-scroll-lock 已知上游 bug** 的**临时补丁**:
//     https://github.com/willmcpo/body-scroll-lock/issues/271
//
//   【可执行的移除判据】
//     跑:  python3 tools/modal-scroll-guard-probe.py
//     看探针输出的最后一行结论:
//       · 打印「仍跳顶 ⇒ 官方未修, 本补丁要留着」 ⇒ **不能移除**。
//       · 打印「不跳了 ⇒ 官方已修, 本补丁可以移除」 ⇒ 说明 Flarum 官方
//         (或它的 body-scroll-lock 依赖升级) 已经修好了这个 bug ⇒ **移除本补丁**。
//
//   【移除时要删的具体 3 处】
//     ① 本文件 js/src/forum/utils/modalScrollGuard.js (整个文件删掉)
//     ② less/forum.less 末尾的段 (整段删掉, 含该段头注释)
//     ③ js/src/forum/index.js 里的那行 side-effect import:
//          import './utils/modalScrollGuard';
//
//   【顺带说明 — 不要动那个 focus 修法】
//     js/src/forum/index.js 里 safeOverride(Modal, 'onready', ...) ——
//     强制所有 modal 聚焦首个 input —— **跟本补丁是两件事, 移除本补丁时不要动它**。
//     它管的是「有 input 的 modal 抢焦点」(iOS 14+ input focus + position:fixed body
//     互相抵消), 本补丁管的是「body 脱离文档流导致 scrollY 被 clamp」。
//     两者互补, 互不替代。
//
// ────────────────────────────────────────────────────────────────────
// 为什么 CSS 和 JS 两半都要
// ────────────────────────────────────────────────────────────────────
//   · 只有 CSS: 能修好「打开 modal 跳顶」, 但**关不掉** ——
//     关闭时 body-scroll-lock 跑 window.scrollTo(0, NaN) ⇒ 位置还是被清零。
//   · 只有 JS: 打开瞬间 body 已经 fixed ⇒ scrollHeight 已塌缩 ⇒ scrollY 已被 clamp 到 0,
//     记下来的是 0, 补回也是 0 ⇒ 救不回来。
//   所以两半必须配套。
//
// ────────────────────────────────────────────────────────────────────
// 时序 (已实测, 照抄这个顺序写)
// ────────────────────────────────────────────────────────────────────
//   Flarum core ModalManager.onupdate() 里的 requestAnimationFrame 回调:
//     ① setAttribute('aria-hidden', 'true')      ← 排队 mutation record
//     ② disableBodyScroll() → 内部**再**排一个 rAF,
//        下一帧才 setPositionFixed() (body → position:fixed)
//   我们的 MutationObserver 回调在 ① 所在 task 结束后的 microtask 里跑
//   ⇒ 早于 ② 的下一帧 ⇒ **读到的 scrollY 一定是打开前的真值**。
//   嵌套 modal: 堆叠期间 aria-hidden 一直是 'true',
//   但 setAttribute 每次都会派发 mutation record (同值也会派),
//   所以「嵌套期间 clearAllBodyScrollLocks() 又跑了一次 scrollTo(NaN,NaN)」
//   也能被本文件侦测到并补回 —— 状态机天然正确, 不需要额外的计数器。
// ====================================================================

(function () {
  'use strict';

  const APP_ID = 'app';
  const HIDDEN_ATTR = 'aria-hidden';

  // 幂等: 同一页面被 import 多次也只装一次 (webpack chunk 重复加载的保险)
  if (window.__zivenCoreModalScrollGuard) return;
  window.__zivenCoreModalScrollGuard = true;
  let observer = null;
  let savedX = 0;
  let savedY = 0;
  let hasSaved = false; // 当前是否持有一份「打开 modal 前的滚动位置」
  let retryTimer = null;
  let retryCount = 0;
  function currentScroll() {
    return {
      x: typeof window.scrollX === 'number' ? window.scrollX : 0,
      y: typeof window.scrollY === 'number' ? window.scrollY : 0
    };
  }

  // 位置跟记住的不一致 ⇒ 补回 (被 body-scroll-lock 的 scrollTo(0, NaN) 毁掉了)
  function restoreIfDrifted() {
    const now = currentScroll();
    if (now.x === savedX && now.y === savedY) return;
    window.scrollTo(savedX, savedY);
  }
  function handleMutation() {
    const app = document.getElementById(APP_ID);
    if (!app) return;
    const hidden = app.getAttribute(HIDDEN_ATTR);
    if (hidden === 'true') {
      // modal 打开 (含嵌套 modal: 属性值没变, 但 setAttribute 仍会派发 record)
      if (!hasSaved) {
        const now = currentScroll();
        savedX = now.x;
        savedY = now.y;
        hasSaved = true;
        return;
      }
      // 嵌套 modal 期间 core 跑了一次 clearAllBodyScrollLocks()
      // ⇒ window.scrollTo(NaN, NaN) ⇒ 位置被清零 ⇒ 补回来
      restoreIfDrifted();
      return;
    }

    // 全部 modal 关闭 (core 把 aria-hidden 移除了)
    if (!hasSaved) return;
    hasSaved = false;
    restoreIfDrifted();
  }
  function attach() {
    if (observer) return;
    const app = document.getElementById(APP_ID);
    if (!app) {
      // #app 还没渲染出来 ⇒ 短间隔重试兜底
      // (正常情况 DOMContentLoaded 时 #app 就在了, 这只是保险)
      if (retryCount < 50) {
        retryCount++;
        retryTimer = window.setTimeout(attach, 100);
      }
      return;
    }
    observer = new MutationObserver(handleMutation);
    observer.observe(app, {
      attributes: true,
      attributeFilter: [HIDDEN_ATTR]
    });
  }
  function start() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', start, {
        once: true
      });
      return;
    }
    attach();
  }
  start();
})();